import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getCycleCount, listCycleCountLines, updateCycleCountLine } from "@/lib/cycle-counts.functions";
import { claimCycleCount, releaseCycleCount } from "@/lib/cycle-counts-claim.functions";
import { RfShell, RfEmpty } from "@/components/rf/rf-shell";
import { ScanInput } from "@/components/ui/scan-input";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { CheckCircle2, Lock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/rf/cycle-count/$id")({
  component: RfCycleCount,
});

function RfCycleCount() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;
  const qc = useQueryClient();
  const getFn = useServerFn(getCycleCount);
  const linesFn = useServerFn(listCycleCountLines);
  const updateFn = useServerFn(updateCycleCountLine);
  const claimFn = useServerFn(claimCycleCount);
  const releaseFn = useServerFn(releaseCycleCount);

  const { data: count, refetch: refetchCount } = useQuery({
    queryKey: ["rf-cc", id],
    queryFn: () => getFn({ data: { id } }),
    refetchInterval: 15_000,
  });
  const { data: bundle, refetch } = useQuery({
    queryKey: ["rf-cc-lines", id],
    queryFn: () => linesFn({ data: { id } }),
  });

  const lines: any[] = (bundle as any)?.lines ?? [];
  const skus: any[] = (bundle as any)?.skus ?? [];
  const locs: any[] = (bundle as any)?.locations ?? [];
  const skuById = useMemo(() => new Map(skus.map((s: any) => [s.id, s])), [skus]);
  const locById = useMemo(() => new Map(locs.map((l: any) => [l.id, l])), [locs]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [claimAttempted, setClaimAttempted] = useState(false);

  const active: any = lines.find((l: any) => l.id === activeId) ?? null;

  const claimedBy: string | null = (count as any)?.claimed_by ?? null;
  const lockedByOther = !!claimedBy && claimedBy !== userId;
  const mine = !!claimedBy && claimedBy === userId;

  // Auto-claim on entry so resume / first-open both feel like one tap.
  // Failure (race with another worker) is surfaced via toast and the
  // locked banner; we don't kick the worker back to the list — the
  // banner makes the state self-explanatory.
  useEffect(() => {
    if (!userId || claimAttempted || claimedBy) return;
    if ((count as any)?.status && (count as any).status !== "open") return;
    setClaimAttempted(true);
    claimFn({ data: { id } })
      .then(() => refetchCount())
      .catch((e: any) => {
        toast.error(e?.message ?? "Could not claim count");
        refetchCount();
      });
  }, [userId, claimAttempted, claimedBy, count, claimFn, id, refetchCount]);

  // Drop-into-action: if only one line on this count is still open, select
  // it automatically so the worker lands directly on the qty input. The
  // line picker still renders when 2+ open lines remain — that's a real
  // operator choice (which bin to count next).
  useEffect(() => {
    if (activeId || lockedByOther) return;
    const openLines = lines.filter((l: any) => l.counted_qty == null);
    if (openLines.length === 1) {
      setActiveId(openLines[0].id);
      setQty("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, lockedByOther, lines.length]);

  async function handleRelease() {
    setBusy(true);
    try {
      await releaseFn({ data: { id } });
      toast.success("Released — anyone can pick this up");
      navigate({ to: "/app/rf/cycle-count" });
    } catch (e: any) {
      toast.error(e?.message ?? "Release failed");
    } finally { setBusy(false); }
  }

  function handleScan(code: string) {
    if (lockedByOther) return;
    const lc = code.trim().toLowerCase();
    const matchByLoc = lines.find((l: any) => {
      const loc: any = locById.get(l.location_id);
      return loc?.code?.toLowerCase() === lc;
    });
    if (matchByLoc) { setActiveId(matchByLoc.id); setQty(""); return; }
    const matchBySku = lines.find((l: any) => {
      const sku: any = skuById.get(l.sku_id);
      return sku?.sku_code?.toLowerCase() === lc;
    });
    if (matchBySku) { setActiveId(matchBySku.id); setQty(""); return; }
    toast.error(`No count line matches "${code}"`);
  }

  async function confirm() {
    if (!active || lockedByOther) return;
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n < 0) return toast.error("Enter a non-negative quantity");
    setBusy(true);
    try {
      await updateFn({ data: { lineId: active.id, countedQty: n } });
      toast.success(`Counted ${n}`);
      setActiveId(null);
      setQty("");
      await refetch();
      qc.invalidateQueries({ queryKey: ["rf-cc-lines", id] });
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (active) {
    if (lockedByOther) {
      setActiveId(null);
      return null;
    }
    const sku: any = skuById.get(active.sku_id);
    const loc: any = locById.get(active.location_id);
    return (
      <RfShell title={(count as any)?.name ?? "Count"} back="/app/rf/cycle-count">
        <div className="rounded-md border bg-muted/30 p-3 space-y-1">
          <div className="text-xs text-muted-foreground">Location</div>
          <div className="font-mono">{loc?.code ?? "—"}</div>
          <div className="text-xs text-muted-foreground mt-2">SKU</div>
          <div className="font-mono">{sku?.sku_code ?? "—"}</div>
          <div className="text-sm">{sku?.name ?? ""}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Expected {active.expected_qty}</div>
        </div>
        <Input
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus
          value={qty}
          onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))}
          className="h-14 text-2xl text-center font-mono"
          placeholder="Counted qty"
        />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => { setActiveId(null); setQty(""); }} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={busy || qty === ""}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Save
          </Button>
        </div>
      </RfShell>
    );
  }

  const pending = lines.filter((l: any) => l.counted_qty == null);
  return (
    <RfShell title={(count as any)?.name ?? "Count"} back="/app/rf/cycle-count">
      {lockedByOther ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex items-center gap-2">
          <Lock className="h-4 w-4" />
          Another worker is counting this. Ask a supervisor to release if it's been abandoned.
        </div>
      ) : (
        <ScanInput onScan={handleScan} placeholder="Scan location or SKU…" size="lg" />
      )}
      <div className="text-xs text-muted-foreground px-1">
        {pending.length} of {lines.length} pending
      </div>
      {mine ? (
        <Button variant="outline" size="sm" onClick={handleRelease} disabled={busy} className="w-full">
          Release count
        </Button>
      ) : null}
      <div className="space-y-2">
        {lines.length === 0 ? (
          <RfEmpty>No count lines. Add from desktop.</RfEmpty>
        ) : (
          lines.map((l: any) => {
            const sku: any = skuById.get(l.sku_id);
            const loc: any = locById.get(l.location_id);
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => { if (!lockedByOther) { setActiveId(l.id); setQty(""); } }}
                disabled={lockedByOther}
                className="flex w-full items-center gap-3 rounded-md border bg-card px-3 py-3 text-left active:bg-muted/70 disabled:opacity-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-sm">{loc?.code ?? "—"}</div>
                  <div className="truncate text-xs text-muted-foreground">{sku?.sku_code ?? "—"}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-base font-semibold tabular-nums">
                    {l.counted_qty ?? "—"}<span className="text-muted-foreground text-xs">/{l.expected_qty}</span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </RfShell>
  );
}